import { QueryClient, QueryKey, useMutation, useQuery, useQueryClient } from "react-query"
import { notification } from "antd"
import * as scheduleApi from "./scheduleApi"
import { CreateScheduledEventByJobRequest, DeleteScheduledEventsRequest, UpdateVisitWorkareaStatusRequest, UpsertVisitRequest } from "./scheduleApi"
import { SaveVisitPayload } from "../visits/editVisitApi"
import { useMutationWithOptimisticUpdate } from '../../hooks/useMutationWithOptimisticUpdate'
import { isSearchable, SEARCH_PAGE_SIZE } from './calendarSearchModel'
import { saveZip } from '../../services/fileSystemService'

export const invalidateEventQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries(['scheduleEventsByDate'])
  queryClient.invalidateQueries(['scheduleEventsByDateRange'])
  queryClient.invalidateQueries(['dayViewEvents'])
  queryClient.invalidateQueries(['dateRangeViewEvents'])
  queryClient.invalidateQueries(['eventDetail'])
  queryClient.invalidateQueries(['consultationsByDateRange'])
  queryClient.invalidateQueries(['tasksByDateRange'])
  queryClient.invalidateQueries(['rescheduleCandidates'])
}

type DateRangeViewQueriesData = [QueryKey, scheduleApi.DateRangeViewResponse | undefined][]

const applyOptimisticEventUpdate = async (
  queryClient: QueryClient,
  eventId: number,
  start: string,
  end: string
): Promise<{ queriesData: DateRangeViewQueriesData }> => {
  await queryClient.cancelQueries(['dateRangeViewEvents'])

  const queriesData = queryClient.getQueriesData<scheduleApi.DateRangeViewResponse>(['dateRangeViewEvents'])

  queriesData.forEach(([queryKey, data]) => {
    if (data) {
      const updatedEvents = data.events.map(event => {
        if (event.id === eventId) {
          return {
            ...event,
            start,
            end,
          }
        }
        return event
      })
      queryClient.setQueryData(queryKey, {
        ...data,
        events: updatedEvents,
      })
    }
  })

  return { queriesData }
}

const rollbackOptimisticEventUpdate = (
  queryClient: QueryClient,
  context: { queriesData: DateRangeViewQueriesData } | undefined
) => {
  if (context?.queriesData) {
    context.queriesData.forEach(([queryKey, data]) => {
      queryClient.setQueryData(queryKey, data)
    })
  }
}


export const useCrewQuery = (tenantId?: number) => {
  return useQuery(['scheduleCrew', tenantId], {
    queryFn: () => scheduleApi.loadCrew(),
    enabled: tenantId !== undefined
  })
}

export const useScheduledEventsByDateQuery = (date: string) => {
  return useQuery(['scheduleEventsByDate', date], {
    queryFn: () => scheduleApi.loadScheduledEventsByDate(date),
    enabled: !!date && date !== ''
  })
}

export const useDayViewEventsQuery = (date: string) => {
  return useQuery(['dayViewEvents', date], {
    queryFn: () => scheduleApi.loadDayViewEvents(date),
    enabled: !!date
  })
}

export const useDateRangeViewEventsQuery = (startDate: string, endDate: string) => {
  return useQuery(['dateRangeViewEvents', startDate, endDate], {
    queryFn: () => scheduleApi.loadDateRangeViewEvents(startDate, endDate),
    enabled: !!startDate && !!endDate
  })
}

export const useEventDetailQuery = (eventId: number | null, onError?: () => void) => {
  return useQuery(['eventDetail', eventId], {
    queryFn: () => scheduleApi.loadEventDetail(eventId as number),
    enabled: eventId !== null,
    retry: false,
    onError: () => {
      notification.error({
        message: 'Could Not Open Event',
        description: 'This event could not be loaded. It may have been removed, or its job may be missing a property.',
      })
      onError?.()
    }
  })
}

export const usePrintCrewDaySchedulesMutation = (onEmptyDay: () => void) => {
  return useMutation({
    mutationFn: (request: scheduleApi.PrintCrewDaySchedulesRequest) => scheduleApi.printCrewDaySchedules(request),
    onSuccess: (result) => {
      if (result.archive === null) {
        onEmptyDay()
        return
      }
      saveZip(result.archive, result.fileName)
    },
    onError: () => {
      notification.error({
        message: 'Download Failed',
        description: 'The crew schedules could not be generated. Please try again.',
      })
    }
  })
}

export const useScheduledEventSearchQuery = (searchTerm: string, page: number) => {
  const trimmed = searchTerm.trim()
  return useQuery(['scheduledEventSearch', trimmed, page], {
    queryFn: () => scheduleApi.searchScheduledEvents(trimmed, page, SEARCH_PAGE_SIZE),
    enabled: isSearchable(trimmed),
    keepPreviousData: true
  })
}

export const useScheduleEventsInvalidator = () => {
  const queryClient = useQueryClient()
  return {
    invalidateAllEvents: () => invalidateEventQueries(queryClient)
  }
}

export const useUpdateScheduledEventCrewLeadMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ eventId, crewLeadEmail, crewMemberIds }: { eventId: number, crewLeadEmail: string | null, crewMemberIds: string | null }) => scheduleApi.updateScheduledEventCrewLead(eventId, crewLeadEmail, crewMemberIds),
    onSuccess: () => {
      invalidateEventQueries(queryClient)
    },
    onError: () => {
      notification.error({
        message: 'Update Failed',
        description: 'Failed to update the crew assignment. Please try again.',
      })
    }
  })
}

export const useUpdateVisitNotesMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: UpsertVisitRequest) => scheduleApi.updateVisitNotes(request),
    onSuccess: () => {
      invalidateEventQueries(queryClient)
    },
    onError: (error: any) => {
      const message = error.response?.data?.message ?? 'There was a problem saving the visit notes.'
      notification.error({ message })
    }
  })
}

export const useUpdateVisitWorkareaStatusMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: UpdateVisitWorkareaStatusRequest) => scheduleApi.updateVisitWorkareaStatus(request),
    onSuccess: () => {
      invalidateEventQueries(queryClient)
    }
  })
}

export const useGetJobItemsQuery = (jobId: number) => {
  return useQuery(['jobItems', jobId], {
    queryFn: () => scheduleApi.getJobItems(jobId),
  })
}

export const useGetJobQuery = (jobId: number) => {
  return useQuery(['job', jobId], {
    queryFn: () => scheduleApi.getJob(jobId),
  })
}

export const useRemoveScheduledEventMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (eventId: number) => scheduleApi.removeScheduledEvent(eventId),
    onSuccess: () => {
      invalidateEventQueries(queryClient)
    }
  })
}

export const useRemoveScheduledEventsByRangeMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ eventId, request }: { eventId: number, request: DeleteScheduledEventsRequest }) => scheduleApi.removeScheduledEventsByRange(eventId, request),
    onSuccess: () => {
      invalidateEventQueries(queryClient)
    }
  })
}

export const useRemoveScheduledEventsMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (eventId: number) => scheduleApi.removeScheduledEvents(eventId),
    onSuccess: () => {
      invalidateEventQueries(queryClient)
    }
  })
}

export const useGetRepeatingEventConfigQuery = (repeatingId: string | null) => {
  return useQuery(['repeatingEventConfig', repeatingId], {
    queryFn: () => scheduleApi.getRepeatingEventConfig(repeatingId!),
    enabled: !!repeatingId
  })
}

export const useCreateVisitForScheduledEventMutation = (onSuccess: () => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ eventId, request }: { eventId: number, request: SaveVisitPayload }) => scheduleApi.createVisitForScheduledEvent(eventId, request),
    onSuccess: () => {
      onSuccess()
      invalidateEventQueries(queryClient)
    },
    onError: (error: any) => {
      const message = error.response?.data?.message ?? 'There was a problem saving the visit.'
      notification.error({ message })
    }
  })
}

export const useGetSchedulingJobsQuery = () => {
  return useQuery(['schedulingJobs'], {
    queryFn: () => scheduleApi.getSchedulingJobs()
  })
}

export const useGetScheduledJobsByDateQuery = (date: string, crewLeadEmail: string, enabled: boolean = true) => {
  return useQuery(['scheduledJobsByDate', date, crewLeadEmail], {
    queryFn: () => scheduleApi.getScheduledJobsByDate(date, crewLeadEmail),
    enabled: enabled && !!date && date !== '' && !!crewLeadEmail && crewLeadEmail !== ''
  })
}

export const useCreateScheduledEventByJobMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: CreateScheduledEventByJobRequest) => scheduleApi.createScheduledEventByJob(request),
    onSuccess: () => {
      queryClient.invalidateQueries(['scheduledJobsByDate'])
      queryClient.invalidateQueries(['schedulingJobs'])
      queryClient.invalidateQueries(['repeatingEventConfig'])
      invalidateEventQueries(queryClient)
    },
    onError: () => {
      notification.error({
        message: 'Failed to Schedule Job',
        description: 'Something went wrong while scheduling the job. Please try again.',
      })
    },
  })
}

export const useCreateScheduledEventsByJobV2Mutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: scheduleApi.CreateScheduledEventsByJobV2Request) => scheduleApi.createScheduledEventsByJobV2(request),
    onSuccess: () => {
      queryClient.invalidateQueries(['scheduledJobsByDate'])
      queryClient.invalidateQueries(['schedulingJobs'])
      queryClient.invalidateQueries(['repeatingEventConfig'])
      invalidateEventQueries(queryClient)
    },
    onError: () => {
      notification.error({
        message: 'Failed to Schedule Job',
        description: 'Something went wrong while scheduling the job. Please try again.',
      })
    },
  })
}

export const useUpsertTaskEventMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: scheduleApi.UpsertTaskEventRequest) => scheduleApi.upsertTaskEvent(request),
    onMutate: async (request) => {
      if (!request.eventId) {
        return
      }

      await queryClient.cancelQueries(['tasksByDateRange'])

      const queriesData = queryClient.getQueriesData<scheduleApi.TaskCalendarResponse>(['tasksByDateRange'])

      queriesData.forEach(([queryKey, data]) => {
        if (data) {
          queryClient.setQueryData(queryKey, {
            ...data,
            tasks: data.tasks.map(task => {
              if (task.id === request.eventId) {
                return {
                  ...task,
                  start: request.start,
                  end: request.end,
                  startUtc: request.start,
                  endUtc: request.end,
                  title: request.title,
                  notes: request.notes,
                  allDay: request.allDay,
                  sendCrewLeadNotifications: request.sendCrewLeadNotifications,
                  completedAt: request.completedAt ?? task.completedAt,
                  frozen: request.frozen,
                }
              }
              return task
            })
          })
        }
      })

      return { queriesData }
    },
    onError: (_error, _variables, context) => {
      if (context?.queriesData) {
        context.queriesData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
    },
    onSettled: () => {
      invalidateEventQueries(queryClient)
    }
  })
}

export const useSetTaskCompletionMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: scheduleApi.SetTaskCompletionRequest) => scheduleApi.setTaskCompletion(request),
    onSuccess: () => {
      invalidateEventQueries(queryClient)
    },
    onError: () => {
      notification.error({
        message: 'Failed to Update Task',
        description: 'Something went wrong while updating the task. Please try again.',
      })
    },
  })
}

export const useBulkUpdateScheduledEventTimeMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: scheduleApi.BulkUpdateScheduledEventTimeRequest) => scheduleApi.bulkUpdateScheduledEventTime(request),
    onSuccess: () => {
      invalidateEventQueries(queryClient)
    }
  })
}

export const useUpdateScheduledEventMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: scheduleApi.UpdateScheduledEventRequest) => scheduleApi.updateScheduledEvent(request),
    onMutate: (request) => applyOptimisticEventUpdate(queryClient, request.eventId, request.start, request.end),
    onError: (_err, _request, context) => rollbackOptimisticEventUpdate(queryClient, context),
    onSettled: () => {
      queryClient.invalidateQueries(['schedulingJobs'])
      queryClient.invalidateQueries(['repeatingEventConfig'])
      invalidateEventQueries(queryClient)
    }
  })
}

export const useUpdateScheduledEventsRangeMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: scheduleApi.UpdateScheduledEventsRangeRequest) => scheduleApi.updateScheduledEventsRange(request),
    onMutate: (request) => applyOptimisticEventUpdate(queryClient, request.updateRequest.eventId, request.updateRequest.start, request.updateRequest.end),
    onError: (_err, _request, context) => rollbackOptimisticEventUpdate(queryClient, context),
    onSettled: () => {
      queryClient.invalidateQueries(['schedulingJobs'])
      queryClient.invalidateQueries(['repeatingEventConfig'])
      invalidateEventQueries(queryClient)
    }
  })
}

export const useUpdateFutureScheduledEventsMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: scheduleApi.UpdateScheduledEventRequest) => scheduleApi.updateFutureScheduledEvents(request),
    onMutate: (request) => applyOptimisticEventUpdate(queryClient, request.eventId, request.start, request.end),
    onError: (_err, _request, context) => rollbackOptimisticEventUpdate(queryClient, context),
    onSettled: () => {
      queryClient.invalidateQueries(['schedulingJobs'])
      queryClient.invalidateQueries(['repeatingEventConfig'])
      invalidateEventQueries(queryClient)
    }
  })
}

export const visitAttachmentsQueryKey = (visitId: string | undefined) => ['visitAttachments', visitId]

export const useGetVisitAttachmentsQuery = (visitId: string | undefined) => {
  return useQuery(visitAttachmentsQueryKey(visitId), {
    queryFn: () => scheduleApi.getVisitAttachments(visitId!),
    enabled: !!visitId
  })
}

export const useDeleteAttachmentMutation = (visitId: string | undefined) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (attachmentId: string) => scheduleApi.deleteAttachment(attachmentId),
    onSuccess: () => {
      queryClient.invalidateQueries(visitAttachmentsQueryKey(visitId))
    },
    onError: () => {
      notification.error({ message: 'There was an error deleting the attachment.' })
    }
  })
}

export const useSchedulingWorkAreasQuery = (jobId: number) => {
  return useQuery(['schedulingWorkAreas', jobId], {
    queryFn: () => scheduleApi.getSchedulingWorkAreas(jobId),
    enabled: !!jobId
  })
}

export const useTasksByDateRangeQuery = (startDate: string, endDate: string) => {
  return useQuery(['tasksByDateRange', startDate, endDate], {
    queryFn: () => scheduleApi.loadTasksByDateRange(startDate, endDate),
    enabled: !!startDate && !!endDate
  })
}

export const useConsultationsByDateRangeQuery = (startDate: string, endDate: string) => {
  return useQuery(['consultationsByDateRange', startDate, endDate], {
    queryFn: () => scheduleApi.loadConsultationsByDateRange(startDate, endDate),
    enabled: !!startDate && !!endDate
  })
}

type UpdateConsultationTimeVariables = {
  id: number
  startUtc: string
  endUtc: string
  start: string
  end: string
  startDate: string
  endDate: string
}

export const useUpdateConsultationTimeMutation = () => {
  const queryClient = useQueryClient()
  return useMutationWithOptimisticUpdate<scheduleApi.ConsultationCalendarResponse, void, Error, UpdateConsultationTimeVariables>({
    mutationFn: (request) => scheduleApi.updateConsultationTime(request.id, request.startUtc, request.endUtc),
    getQueryKey: (variables) => ['consultationsByDateRange', variables.startDate, variables.endDate],
    getUpdatedQueryData: (variables, previousData) => {
      if (!previousData) {
        return { consultations: [] }
      }
      return {
        ...previousData,
        consultations: previousData.consultations.map(consultation => {
          if (consultation.id === variables.id) {
            return {
              ...consultation,
              start: variables.start,
              end: variables.end,
            }
          }
          return consultation
        })
      }
    },
    onSettled: () => {
      invalidateEventQueries(queryClient)
    }
  })
}

export const useRescheduleCandidatesQuery = (startDate: string, endDate: string) => {
  return useQuery(['rescheduleCandidates', startDate, endDate], {
    queryFn: () => scheduleApi.getRescheduleCandidates(startDate, endDate),
    enabled: !!startDate && !!endDate
  })
}

export const useRescheduleEventMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: scheduleApi.RescheduleEventRequest) => scheduleApi.rescheduleEvent(request),
    onSuccess: () => {
      queryClient.invalidateQueries(['scheduledJobsByDate'])
      queryClient.invalidateQueries(['schedulingJobs'])
      invalidateEventQueries(queryClient)
    },
    onError: () => {
      notification.error({
        message: 'Failed to Reschedule',
        description: 'Something went wrong while rescheduling the event. Please try again.',
      })
    },
  })
}